// The interface language (docs/plans/phase3-portal-feedback-kiswahili.md §8; ADR 0035).
//
// A tiny in-house layer rather than a library: typed message keys (a missing Kiswahili key
// fails typecheck), `{name}` placeholders, and a React context. Nothing here formats dates or
// money: those keep the house formats (`22 Sep 2026`, `14:05`, `KES 12,500`) in every
// language, through the existing formatters.
//
// Where the language comes from: the signed-in person's setting (session.language) on staff
// screens, the portal account's on portal screens, and before anyone signs in the choice last
// made on this device (a convenience only: it may be missing).

import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';

import { ApiError } from '../api/client.ts';
import { en, type MessageKey } from './messages/en.ts';
import { sw } from './messages/sw.ts';

export type Language = 'en' | 'sw';
export const LANGUAGES: readonly Language[] = ['en', 'sw'];

const CATALOGUES: Record<Language, Record<MessageKey, string>> = { en, sw };
const STORAGE_KEY = 'wodi.language';

export type Params = Record<string, string | number>;

/** The message in `language`, with `{name}` placeholders filled from `params`. */
export function translate(language: Language, key: MessageKey, params: Params = {}): string {
  const text = CATALOGUES[language][key];
  return text.replace(/\{(\w+)\}/g, (whole, name: string) =>
    Object.hasOwn(params, name) ? String(params[name]) : whole,
  );
}

export function isLanguage(value: unknown): value is Language {
  return value === 'en' || value === 'sw';
}

/** The language last chosen on this device, or English. */
export function storedLanguage(): Language {
  try {
    const value = window.localStorage.getItem(STORAGE_KEY);
    return isLanguage(value) ? value : 'en';
  } catch {
    return 'en';
  }
}

function storeLanguage(language: Language): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, language);
  } catch {
    // A convenience only: without storage the next visit starts in English.
  }
}

export type Translate = (key: MessageKey, params?: Params) => string;

interface I18nValue {
  language: Language;
  setLanguage: (language: Language) => void;
  t: Translate;
}

const I18nContext = createContext<I18nValue>({
  language: 'en',
  setLanguage: () => undefined,
  t: (key, params) => translate('en', key, params),
});

export function I18nProvider({
  children,
  initial,
}: {
  children: ReactNode;
  /** Tests pass a language; the app starts from this device's last choice. */
  initial?: Language;
}) {
  const [language, setState] = useState<Language>(() => initial ?? storedLanguage());
  const setLanguage = useCallback((next: Language) => {
    setState(next);
    storeLanguage(next);
    document.documentElement.lang = next;
  }, []);
  const value = useMemo<I18nValue>(
    () => ({
      language,
      setLanguage,
      t: (key, params) => translate(language, key, params),
    }),
    [language, setLanguage],
  );
  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n(): I18nValue {
  return useContext(I18nContext);
}

export function useT(): Translate {
  return useContext(I18nContext).t;
}

const TRANSLATED_ERRORS: ReadonlySet<string> = new Set([
  'offline',
  'not_signed_in',
  'forbidden',
  'rate_limited',
  'service_unavailable',
  'internal_error',
  'not_found',
  'invalid_credentials',
]);

/**
 * What to tell the person about a failure. In English the server's message is the most
 * specific, so it is shown as it is. In another language, the common errors have their own
 * words; the rest keep the server's English message until their screens are translated.
 */
export function errorText(t: Translate, language: Language, error: unknown): string {
  if (!(error instanceof ApiError)) return t('error.generic');
  if (language === 'en' || !TRANSLATED_ERRORS.has(error.code)) return error.message;
  const text = t(`errors.${error.code}` as MessageKey);
  return error.requestId === null ? text : `${text} Reference: ${error.requestId}`;
}
